import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

import { type FacilityNote } from "../types";

const FacilityNoteAttributes = z.object({
  workplaceId: z.string(),
  categoryId: z.string(),
  rules: z.array(
    z.object({
      text: z.string(),
      quizQuestionId: z.string().optional(),
    })
  ),
});

const useSaveFacilityRulesAndQuizRequestSchema = z.object({
  "atomic:operations": z.array(
    z.union([
      // Workplace operations
      z.object({
        op: z.literal("update"),
        data: z.object({
          id: z.string(),
          type: z.literal("workplace"),
          attributes: z.object({
            quizWorkersOnRules: z.boolean(),
          }),
        }),
      }),
      // Quiz operation
      z.object({
        op: z.literal("add"),
        data: z.object({
          type: z.literal("WorkplaceQuiz"),
          attributes: z.object({
            workplaceId: z.string(),
            quiz: z.object({
              questions: z.array(
                z.object({
                  id: z.string(),
                  text: z.string(),
                  options: z.array(
                    z.object({
                      text: z.string(),
                      isCorrect: z.boolean(),
                    })
                  ),
                })
              ),
            }),
          }),
        }),
      }),
      // Facility Note operations
      z.object({
        op: z.literal("add"),
        data: z.object({
          type: z.literal("FacilityNote"),
          attributes: FacilityNoteAttributes,
        }),
      }),
      z.object({
        op: z.literal("update"),
        data: z.object({
          id: z.string(),
          type: z.literal("FacilityNote"),
          attributes: FacilityNoteAttributes,
        }),
      }),
      z.object({
        op: z.literal("remove"),
        data: z.object({
          id: z.string(),
          type: z.literal("FacilityNote"),
          attributes: z.object({
            workplaceId: z.string(),
          }),
        }),
      }),
    ])
  ),
});

const useSaveFacilityRulesAndQuizResponseSchema = z.object({
  "atomic:results": z.array(
    z.object({
      data: z.object({
        id: z.string(),
        type: z.union([z.literal("WorkplaceQuiz"), z.literal("FacilityNote")]),
      }),
    })
  ),
});

export const OPERATIONS_URL = `${environmentConfig.REACT_APP_API_MAIN_URL}/api/operations`;

interface SaveFacilityRulesAndQuizParams {
  workplaceId: string;
  oldFacilityNotes: FacilityNote[];
  newFacilityNotes: FacilityNote[];
  questions: Array<{
    id: string;
    text: string;
    options: Array<{
      text: string;
      isCorrect: boolean;
    }>;
  }>;
}

type AtomicOperations = z.infer<
  typeof useSaveFacilityRulesAndQuizRequestSchema
>["atomic:operations"];

// Check if a facility note should be added
function shouldAddFacilityNote(oldNote: FacilityNote, newNote: FacilityNote): boolean {
  // Add a new note if the old note doesn't exist and the new note has rules
  return (
    !isDefined(oldNote.noteId) &&
    isDefined(newNote.rules) &&
    newNote.rules.length > 0 &&
    isDefined(newNote.categoryId)
  );
}

// Check if a facility note should be removed
function shouldRemoveFacilityNote(oldNote: FacilityNote, newNote: FacilityNote): boolean {
  // Remove the note if the old note has rules and the new note has no rules
  return (
    isDefined(oldNote.rules) &&
    oldNote.rules.length > 0 &&
    (!isDefined(newNote.rules) || newNote.rules.length === 0) &&
    isDefined(oldNote.noteId)
  );
}

function getOperations(
  params: SaveFacilityRulesAndQuizParams
): z.infer<typeof useSaveFacilityRulesAndQuizRequestSchema> {
  const { workplaceId, oldFacilityNotes, newFacilityNotes, questions } = params;
  const operations: AtomicOperations = [];

  // Add quiz operation if there are questions.
  if (questions.length > 0) {
    operations.push({
      op: "add",
      data: {
        type: "WorkplaceQuiz",
        attributes: {
          workplaceId,
          quiz: {
            questions: questions.map((question) => ({
              ...question,
              options: question.options.map(({ text, isCorrect }) => ({
                text,
                isCorrect,
              })),
            })),
          },
        },
      },
    });
  }

  operations.push({
    op: "update",
    data: {
      id: workplaceId,
      type: "workplace",
      attributes: {
        quizWorkersOnRules: questions.length > 0,
      },
    },
  });

  const noteIdentifiers = new Set([
    ...oldFacilityNotes.map((note) => note.identifier),
    ...newFacilityNotes.map((note) => note.identifier),
  ]);

  // Add facility notes operations
  // eslint-disable-next-line unicorn/prefer-spread
  for (const noteIdentifier of Array.from(noteIdentifiers)) {
    const oldNote = oldFacilityNotes.find((note) => note.identifier === noteIdentifier);
    const newNote = newFacilityNotes.find((note) => note.identifier === noteIdentifier);

    if (!isDefined(oldNote) || !isDefined(newNote)) {
      // Should not happen. Both the old and new notes are created from the same list of categories.
      continue;
    }

    if (shouldAddFacilityNote(oldNote, newNote)) {
      const noteRules = (newNote.rules ?? []).map((rule) => ({
        text: rule.text,
        quizQuestionId: rule.quizQuestionId,
      }));
      operations.push({
        op: "add",
        data: {
          type: "FacilityNote",
          attributes: {
            categoryId: newNote.categoryId,
            workplaceId,
            rules: noteRules,
          },
        },
      });
    } else if (shouldRemoveFacilityNote(oldNote, newNote)) {
      operations.push({
        op: "remove",
        data: {
          id: oldNote.noteId!,
          type: "FacilityNote",
          attributes: {
            workplaceId,
          },
        },
      });
    } else if (isDefined(oldNote.noteId)) {
      const rules = (newNote.rules ?? []).map((rule) => ({
        text: rule.text,
        quizQuestionId: rule.quizQuestionId,
      }));
      operations.push({
        op: "update",
        data: {
          id: oldNote.noteId,
          type: "FacilityNote",
          attributes: {
            categoryId: oldNote.categoryId,
            workplaceId,
            rules,
          },
        },
      });
    }
  }

  return {
    "atomic:operations": operations,
  };
}

export function useSaveFacilityRulesAndQuiz(
  options: UseMutationOptions<void, string, SaveFacilityRulesAndQuizParams> = {}
) {
  return useMutation({
    mutationFn: async (params) => {
      const data = getOperations(params);

      if (data["atomic:operations"].length === 0) {
        return; // Nothing to update
      }

      await post({
        url: OPERATIONS_URL,
        data,
        requestSchema: useSaveFacilityRulesAndQuizRequestSchema,
        responseSchema: useSaveFacilityRulesAndQuizResponseSchema,
      });
    },
    ...options,
  });
}
